import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.browser.window
import kotlinx.browser.document
import kotlinx.browser.localStorage
import org.w3c.dom.HTMLScriptElement
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.Blob

// SUPABASE_URL / ANON_KEY come from the generated Config.kt (see build.gradle.kts) so a
// production build can point at Supabase Cloud without touching source; the local defaults there
// still match the local dev stack.

val DUEL_SEQUENCE = listOf(
    "A" to "X", "B" to "Y", "C" to "Z",
    "B" to "X", "A" to "Z", "C" to "Y",
    "B" to "Z", "C" to "X", "A" to "Y",
)

val httpClient = HttpClient(Js) {
    expectSuccess = true
    install(ContentNegotiation) {
        // encodeDefaults=true -- otherwise kotlinx.serialization drops any field that equals its
        // Kotlin-side default (e.g. `registered: Boolean = false`) from the JSON body entirely, so
        // PATCHing a checkbox back to false silently omits that column and PostgREST leaves the
        // previous (true) value untouched instead of updating it.
        json(Json { ignoreUnknownKeys = true; encodeDefaults = true })
    }
}

// Scoped independently of any one caller's LaunchedEffect/coroutine, so an in-flight request
// started by one composable keeps running (and stays shareable) even if a *different* caller that
// awaits it gets cancelled. The request removes itself on completion, not when an awaiting
// screen leaves; a newly mounted screen can still join the same request.
private val inFlightScope = kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.SupervisorJob())
private val inFlightRequests = mutableMapOf<String, kotlinx.coroutines.Deferred<*>>()

/**
 * Coalesces concurrent identical GET requests into one network call: if two composables mount at
 * (or near) the same moment and both ask for the same [key], the second attaches to the first's
 * already-in-flight request instead of firing a duplicate. This is *not* a cache -- there is no
 * TTL and nothing is kept once the request settles, so the very next call for the same key always
 * hits the network fresh. Safe because Kotlin/JS is single-threaded: the check-then-register below
 * has no suspension point in between, so two "simultaneous" callers can't both start a fetch.
 */
@Suppress("UNCHECKED_CAST")
private suspend fun <T> singleFlight(key: String, block: suspend () -> T): T {
    (inFlightRequests[key] as? kotlinx.coroutines.Deferred<T>)?.let { return it.await() }
    val deferred = inFlightScope.async { block() }
    inFlightRequests[key] = deferred
    deferred.invokeOnCompletion {
        if (inFlightRequests[key] === deferred) inFlightRequests.remove(key)
    }
    return deferred.await()
}

// key -> (last ETag seen, raw JSON body it tagged). The cache-backed Edge Functions (standings,
// player-stats, player-of-round) tag their responses with an ETag derived from their DB cache rows'
// computed_at, so a repeat request for unchanged data comes back as a bodyless 304 -- this is what
// lets the client actually skip the payload transfer instead of just skipping recomputation
// server-side, which the DB-level cache alone already handled.
//
// The map is the in-session tier; a localStorage tier below it (etagGet/etagPut) makes the 304 path
// survive a browser restart too. Both tiers are namespaced by build version (see etagLsKey): a new
// deploy = cold cache, so a changed response DTO shape can never be re-parsed from a stale body that
// the server would still 304 (its ETag is derived from data's computed_at, not from the DTO shape).
private val etagStore = mutableMapOf<String, Pair<String, String>>()

private const val ETAG_NS = "etag:"

/** The `?v=<hash>` versionStaticAssets stamps onto composeApp.js at build time, read back at runtime;
 *  "dev" when absent (local dev build isn't stamped). Namespaces the persistent cache per build. */
private val buildVersion: String by lazy {
    try {
        val scripts = document.getElementsByTagName("script")
        var v = "dev"
        for (i in 0 until scripts.length) {
            val src = (scripts.item(i) as? HTMLScriptElement)?.src ?: continue
            if (src.contains("composeApp.js")) {
                val q = src.substringAfter("?v=", "")
                if (q.isNotEmpty()) { v = q.substringBefore("&"); break }
            }
        }
        v
    } catch (_: Throwable) { "dev" }
}

private fun etagLsKey(reqKey: String) = "${ETAG_NS}v$buildVersion:$reqKey"

@Serializable private data class EtagEntry(val e: String, val b: String)

// One-shot cleanup of persisted entries left by a previous build (different version prefix), so
// localStorage doesn't accumulate dead caches across deploys. Runs on first conditionalGet.
private var etagPruned = false
private fun pruneStaleEtagEntries() {
    if (etagPruned) return
    etagPruned = true
    try {
        val keep = "${ETAG_NS}v$buildVersion:"
        val stale = (0 until localStorage.length)
            .mapNotNull { localStorage.key(it) }
            .filter { it.startsWith(ETAG_NS) && !it.startsWith(keep) }
        stale.forEach { localStorage.removeItem(it) }
    } catch (_: Throwable) { /* storage unavailable/disabled -- ignore */ }
}

/** Reads the (etag, body) pair: in-memory first, then localStorage (hydrating memory on hit). */
private fun etagGet(key: String): Pair<String, String>? {
    etagStore[key]?.let { return it }
    return try {
        val raw = localStorage.getItem(etagLsKey(key)) ?: return null
        val entry = apiErrorJson.decodeFromString(EtagEntry.serializer(), raw)
        (entry.e to entry.b).also { etagStore[key] = it }
    } catch (_: Throwable) { null }
}

/** Writes both tiers. On a localStorage failure (quota/full/disabled) drops our whole namespace and
 *  retries once, so a full cache can't wedge future writes; the in-memory tier always succeeds. */
private fun etagPut(key: String, etag: String, body: String) {
    etagStore[key] = etag to body
    val lsKey = etagLsKey(key)
    val value = apiErrorJson.encodeToString(EtagEntry.serializer(), EtagEntry(etag, body))
    try {
        localStorage.setItem(lsKey, value)
    } catch (_: Throwable) {
        try {
            (0 until localStorage.length).mapNotNull { localStorage.key(it) }
                .filter { it.startsWith(ETAG_NS) }
                .forEach { localStorage.removeItem(it) }
            localStorage.setItem(lsKey, value)
        } catch (_: Throwable) { /* give up on persistence; memory tier still holds it */ }
    }
}

/**
 * Conditional GET: resends the last ETag we saw for [key] as If-None-Match, and on a 304 returns
 * the body we already have instead of re-parsing an (identical) fresh one. expectSuccess is turned
 * off for this call only, since Ktor's default validator treats any non-2xx status -- 304 included
 * -- as a failure; genuine errors are re-thrown as ResponseException so existing `.apiMessage()`
 * call sites keep working unchanged.
 */
private suspend inline fun <reified T> conditionalGet(
    key: String,
    url: String,
    noinline block: HttpRequestBuilder.() -> Unit = {},
): T {
    pruneStaleEtagEntries()
    val stored = etagGet(key)
    val response = httpClient.get(url) {
        expectSuccess = false
        block()
        stored?.let { (etag, _) -> header(HttpHeaders.IfNoneMatch, etag) }
    }
    if (response.status.value == 304 && stored != null) {
        return apiErrorJson.decodeFromString(stored.second)
    }
    if (!response.status.isSuccess()) {
        throw ResponseException(response, response.bodyAsText())
    }
    val bodyText = response.bodyAsText()
    response.headers[HttpHeaders.ETag]?.let { etag -> etagPut(key, etag, bodyText) }
    return apiErrorJson.decodeFromString(bodyText)
}

@Serializable
private data class ApiErrorBody(val message: String? = null)

private val apiErrorJson = Json { ignoreUnknownKeys = true }

/** Tour's PostgREST RPC uses a validator envelope instead of HTTP 304 (RPCs are POSTs).
 * Reuse the league cache's build-scoped memory/localStorage tiers and single-flight requests.
 * Every read revalidates on the server; an error never silently serves stale standings. */
internal suspend fun cachedTourPublicResults(kind: String, id: String, final: Boolean = false): String {
    val key = "tour:$SUPABASE_URL:$kind:$id:$final"
    return singleFlight(key) {
        pruneStaleEtagEntries()
        val stored = etagGet(key)
        val response = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_cached_public_results") {
            tourAuth(null)
            setBody(buildJsonObject {
                put("p_kind", kind); put("p_id", id); put("p_final", final)
                stored?.let { put("p_etag", it.first) }
            })
        }.body<kotlinx.serialization.json.JsonObject>()
        if (response.flag("unchanged")) {
            checkNotNull(stored) { "Tour cache validator returned without a saved body" }.second
        } else {
            val body = checkNotNull(response["body"]) { "Missing Tour cache body" }.toString()
            response.text("etag").takeIf { it.isNotEmpty() }?.let { etagPut(key, it, body) }
            body
        }
    }
}

/** Extracts PostgREST's/the RPC's actual error message (e.g. from a raised exception) instead of Ktor's verbose default. */
suspend fun Throwable.apiMessage(): String {
    val bodyText = (this as? ResponseException)?.response?.bodyAsText() ?: return message ?: "neznámá chyba"
    return try {
        apiErrorJson.decodeFromString<ApiErrorBody>(bodyText).message ?: bodyText
    } catch (e: Throwable) {
        bodyText
    }
}

@Serializable
data class TokenResponse(
    @SerialName("access_token") val accessToken: String? = null,
    @SerialName("error_description") val errorDescription: String? = null,
)

@Serializable
data class SeasonDto(
    val id: String,
    val name: String,
    @SerialName("start_date") val startDate: String,
    val status: String,
    @SerialName("is_active") val isActive: Boolean,
)

@Serializable
data class CreateSeasonRequest(val name: String, @SerialName("start_date") val startDate: String)

@Serializable
data class SeasonLeagueDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    val name: String,
    @SerialName("team_count") val teamCount: Int,
    @SerialName("has_playoff") val hasPlayoff: Boolean = false,
    val tier: Int? = null,
    @SerialName("schedule_algorithm") val scheduleAlgorithm: String = "berger",
)

@Serializable
data class CreateLeagueRequest(
    @SerialName("season_id") val seasonId: String,
    val name: String,
    @SerialName("team_count") val teamCount: Int,
    @SerialName("has_playoff") val hasPlayoff: Boolean = false,
    val tier: Int? = null,
    @SerialName("schedule_algorithm") val scheduleAlgorithm: String = "berger",
)

@Serializable
data class UpdateLeagueRequest(
    @SerialName("has_playoff") val hasPlayoff: Boolean,
    @SerialName("team_count") val teamCount: Int,
    val tier: Int? = null,
    @SerialName("schedule_algorithm") val scheduleAlgorithm: String,
)

@Serializable
data class PlayoffSplitRequest(@SerialName("leagueId") val leagueId: String)

@Serializable
data class PlayoffSplitResponseDto(
    val ok: Boolean,
    @SerialName("titleGroup") val titleGroup: List<String>,
    @SerialName("relegationGroup") val relegationGroup: List<String>,
)

@Serializable
data class TeamDto(
    val id: String,
    val name: String,
    @SerialName("average_score") val averageScore: Double? = null,
)

@Serializable
data class CreateTeamRequest(val name: String, @SerialName("average_score") val averageScore: Double? = null)

@Serializable
data class UpdateTeamRequest(val name: String, @SerialName("average_score") val averageScore: Double? = null)

@Serializable
data class SeasonLeagueTeamDto(
    val id: String,
    @SerialName("season_league_id") val seasonLeagueId: String,
    @SerialName("team_id") val teamId: String,
    val seed: Int? = null,
    val team: TeamDto? = null,
    @SerialName("playoff_group") val playoffGroup: String? = null,
    @SerialName("average_score") val averageScore: Double? = null,
    @SerialName("team_name_override") val teamNameOverride: String? = null,
)

@Serializable
data class UpdateSeasonLeagueTeamAverageRequest(@SerialName("average_score") val averageScore: Double?)

@Serializable
data class CloneSeasonRequest(
    val name: String,
    @SerialName("startDate") val startDate: String,
    @SerialName("previousSeasonId") val previousSeasonId: String,
    @SerialName("promotionCount") val promotionCount: Int,
)

@Serializable
data class CloneSeasonLeagueSummaryDto(val name: String, val teamCount: Int)

@Serializable
data class CloneSeasonResponseDto(
    val ok: Boolean,
    @SerialName("seasonId") val seasonId: String,
    val leagues: List<CloneSeasonLeagueSummaryDto>,
)

@Serializable
data class AssignTeamRequest(
    @SerialName("season_league_id") val seasonLeagueId: String,
    @SerialName("team_id") val teamId: String,
)

@Serializable
data class PlayerDto(
    val id: String,
    val name: String,
    @SerialName("full_name") val fullName: String? = null,
    val gender: String? = null,
    val registered: Boolean = false,
    val card: Boolean = false,
    val email: String? = null,
    val phone: String? = null,
)

@Serializable
data class CreatePlayerRequest(
    val name: String,
    @SerialName("full_name") val fullName: String? = null,
    val gender: String? = null,
    val registered: Boolean = false,
    val card: Boolean = false,
    val email: String? = null,
    val phone: String? = null,
)

@Serializable
data class UpdatePlayerRequest(
    val name: String,
    @SerialName("full_name") val fullName: String? = null,
    val gender: String? = null,
    val registered: Boolean = false,
    val card: Boolean = false,
    val email: String? = null,
    val phone: String? = null,
)

fun genderLabel(gender: String?): String = when (gender) {
    "male" -> "Muž"
    "female" -> "Žena"
    else -> "—"
}

/** Formats an ISO "YYYY-MM-DD" date (as returned by Postgres) into the Czech "DD.MM.YYYY" convention. */
fun formatDate(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    val (y, m, d) = parts
    return "$d.$m.$y"
}

/** "12.1.2026" for a single day, "12.1.2026 – 25.1.2026" for a range (spec 11.3). */
fun dateRangeLabel(from: String, to: String?): String =
    if (to.isNullOrBlank() || to == from) formatDate(from) else "${formatDate(from)} – ${formatDate(to)}"

// Kotlin/JS has no java.time; date-only ISO strings parse as UTC midnight, so day-level arithmetic
// on them is safe/consistent regardless of the browser's local timezone.
fun isoEpochDay(iso: String): Double = kotlin.js.Date(iso).getTime() / 86_400_000.0

fun todayIso(): String = kotlin.js.Date().toISOString().substringBefore("T")

@Serializable
data class RosterEntryDto(
    val id: String,
    @SerialName("player_id") val playerId: String,
    @SerialName("team_id") val teamId: String,
    @SerialName("season_id") val seasonId: String,
    val captain: Boolean = false,
    val player: PlayerDto? = null,
)

@Serializable
private data class SetRosterCaptainRequest(
    @SerialName("p_roster_id") val rosterId: String,
    @SerialName("p_captain") val captain: Boolean,
)

@Serializable
data class AddRosterRequest(
    @SerialName("player_id") val playerId: String,
    @SerialName("team_id") val teamId: String,
    @SerialName("season_id") val seasonId: String,
)

@Serializable
data class MatchDto(
    val id: String,
    @SerialName("home_team_id") val homeTeamId: String,
    @SerialName("away_team_id") val awayTeamId: String,
    @SerialName("round_number") val roundNumber: Int,
    val phase: String,
    val status: String,
    @SerialName("home_points") val homePoints: Int? = null,
    @SerialName("away_points") val awayPoints: Int? = null,
    @SerialName("home_duel_points") val homeDuelPoints: Int? = null,
    @SerialName("away_duel_points") val awayDuelPoints: Int? = null,
    @SerialName("home_total_score") val homeTotalScore: Int? = null,
    @SerialName("away_total_score") val awayTotalScore: Int? = null,
    @SerialName("played_at") val playedAt: String? = null,
    @SerialName("is_forfeit") val isForfeit: Boolean = false,
    val result: String? = null,
    @SerialName("protocol_image_path") val protocolImagePath: String? = null,
)

@Serializable
data class StandingsRowDto(
    val teamId: String,
    val teamName: String,
    val played: Int,
    val gamesPlayed: Int = 0,
    val wins: Int,
    val draws: Int,
    val losses: Int,
    val forfeitLosses: Int,
    val points: Int,
    val totalScoreFor: Int,
    val rank: Int,
    val tieBreakChip: String? = null,
)

@Serializable
data class StandingsResponseDto(val standings: List<StandingsRowDto>, val cached: Boolean)

@Serializable
data class DuelInput(
    val homePlayed: Boolean,
    val awayPlayed: Boolean,
    val homeScore: Int?,
    val awayScore: Int?,
)

@Serializable
data class LineupInput(
    val A: String? = null,
    val B: String? = null,
    val C: String? = null,
    val X: String? = null,
    val Y: String? = null,
    val Z: String? = null,
)

@Serializable
data class MatchResultRequest(
    val matchId: String,
    val duels: List<DuelInput> = emptyList(),
    val lineup: LineupInput? = null,
    val playedAt: String? = null,
    val forfeit: String? = null,
    val protocolImagePath: String? = null,
    // The edge function's own SUPABASE_URL env var is the internal container-to-container address
    // locally (e.g. http://kong:8000, unreachable from a browser) -- not safe to embed in the
    // Aktualita's photo link. The browser always has the *correct* public one, so it's sent along
    // here instead; defaults automatically for submitForfeit() too since it shares this data class.
    val protocolImageBaseUrl: String = SUPABASE_URL,
)

@Serializable
data class LeagueRefDto(val name: String)

@Serializable
data class MatchWithLeagueDto(
    val id: String,
    @SerialName("season_league_id") val seasonLeagueId: String,
    @SerialName("home_team_id") val homeTeamId: String,
    @SerialName("away_team_id") val awayTeamId: String,
    @SerialName("round_number") val roundNumber: Int,
    val phase: String,
    val status: String,
    @SerialName("home_points") val homePoints: Int? = null,
    @SerialName("away_points") val awayPoints: Int? = null,
    @SerialName("home_duel_points") val homeDuelPoints: Int? = null,
    @SerialName("away_duel_points") val awayDuelPoints: Int? = null,
    @SerialName("season_league") val seasonLeague: LeagueRefDto? = null,
)

@Serializable
data class PublicSeasonOverviewDto(
    @SerialName("team_count") val teamCount: Int,
    @SerialName("active_player_count") val activePlayerCount: Int,
    @SerialName("round_slots") val roundSlots: List<SeasonRoundSlotDto>,
    @SerialName("round_progress") val roundProgress: List<RoundProgressDto>,
)

@Serializable
data class RoundProgressDto(
    @SerialName("round_number") val roundNumber: Int,
    @SerialName("match_count") val matchCount: Int,
    @SerialName("incomplete_count") val incompleteCount: Int,
)

@Serializable
data class MatchDuelDto(
    val sequence: Int,
    @SerialName("home_position") val homePosition: String,
    @SerialName("away_position") val awayPosition: String,
    @SerialName("home_played") val homePlayed: Boolean,
    @SerialName("away_played") val awayPlayed: Boolean,
    @SerialName("home_score") val homeScore: Int? = null,
    @SerialName("away_score") val awayScore: Int? = null,
)

@Serializable
data class MatchLineupDto(
    val position: String,
    @SerialName("player_id") val playerId: String,
    val player: PlayerDto? = null,
)

@Serializable
data class PlayerStatDto(
    val playerId: String,
    val playerName: String,
    val teamId: String,
    val teamName: String,
    val games: Int,
    val average: Double,
    val minScore: Int,
    val maxScore: Int,
    val teamMatchesPlayed: Int = 0,
)

@Serializable
data class PlayerStatsResponseDto(val players: List<PlayerStatDto>)

data class RankedPlayerStats(val ranked: List<PlayerStatDto>, val unranked: List<PlayerStatDto>)

/**
 * Splits players into a ranked group and an unranked "played too little" group: a player needs
 * at least 30 % of their team's theoretical max games (completed team matches × 3 lineup slots)
 * to be ranked — otherwise they're listed at the end, still sorted by average, but without a
 * rank number.
 */
fun partitionByGamesThreshold(players: List<PlayerStatDto>): RankedPlayerStats {
    val (ranked, unranked) = players.partition { p ->
        val maxGames = p.teamMatchesPlayed * 3
        maxGames <= 0 || p.games >= 0.3 * maxGames
    }
    return RankedPlayerStats(ranked, unranked.sortedByDescending { it.average })
}

@Serializable
data class RoundNomineeDto(
    val playerId: String,
    val playerName: String,
    val teamId: String,
    val teamName: String,
    val totalScore: Int,
)

@Serializable
data class PlayerOfRoundResponseDto(val isFinal: Boolean, val nominees: List<RoundNomineeDto>)

/** One league's one round, as returned by the season-batch mode of player-of-round. */
@Serializable
data class SeasonRoundNomineesDto(
    val leagueId: String,
    val leagueName: String,
    val phase: String,
    val round: Int,
    val isFinal: Boolean,
    val nominees: List<RoundNomineeDto>,
)

@Serializable
data class PlayerOfRoundSeasonBatchDto(val rounds: List<SeasonRoundNomineesDto>)

fun phaseOrder(phase: String): Int = when (phase) {
    "autumn" -> 0
    "spring" -> 1
    else -> 2 // playoff_title / playoff_relegation -- always after the base part
}

private fun phaseName(phase: String): String = when (phase) {
    "autumn" -> "Podzim"
    "spring" -> "Jaro"
    "playoff_title" -> "Nadstavba (O titul)"
    "playoff_relegation" -> "Nadstavba (O udržení)"
    else -> phase
}

/** Match-level label -- distinguishes O titul/O udržení since they're different pairings on the same round. */
fun roundLabel(phase: String, roundNumber: Int): String = "${phaseName(phase)} — kolo $roundNumber"

/**
 * Grouped/list-view label for a round of matches. Unlike roundLabel, this never distinguishes
 * O titul/O udržení: they share the same round number and calendar date (spec 12.2/12.6), so views
 * that group or list matches by round treat them as one round, not two.
 */
fun roundSlotLabel(phase: String, roundNumber: Int): String =
    if (phase == "autumn" || phase == "spring") roundLabel(phase, roundNumber) else "Nadstavba — kolo $roundNumber"

/**
 * Collapses playoff_relegation onto playoff_title -- O titul and O udržení share the same round
 * number and calendar date (spec 12.2/12.6), so views that group/list by round (Rozlosování, Hráč
 * kola) should treat them as ONE round, not two. Does NOT touch match.phase itself, which must stay
 * distinct for standings (spec 12.3) -- this is purely a display/grouping normalization.
 */
fun canonicalPlayoffPhase(phase: String): String = if (phase == "playoff_relegation") "playoff_title" else phase

fun roundKey(phase: String, roundNumber: Int): String = "$phase:$roundNumber"

/** Distinct (phase, roundNumber) pairs present in `matches`, ordered podzim before jaro. */
fun roundOptionsOf(matches: List<MatchDto>): List<Pair<String, Int>> =
    matches.map { it.phase to it.roundNumber }.distinct().sortedWith(compareBy({ phaseOrder(it.first) }, { it.second }))

// ---------- Termínová listina (spec section 11) ----------

@Serializable
data class SeasonRoundSlotDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    @SerialName("round_number") val roundNumber: Int,
    @SerialName("date_from") val dateFrom: String,
    @SerialName("date_to") val dateTo: String? = null,
    /** Empty = shared round for every league in the season. Non-empty = extra round exclusive to these leagues. */
    @SerialName("season_round_slot_league") val leagueLinks: List<RoundSlotLeagueLinkDto> = emptyList(),
) {
    val leagueIds: List<String> get() = leagueLinks.map { it.seasonLeagueId }
}

@Serializable
data class RoundSlotLeagueLinkDto(@SerialName("season_league_id") val seasonLeagueId: String)

@Serializable
data class SeasonEventDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    val title: String,
    val kind: String = "other",
    @SerialName("date_from") val dateFrom: String,
    @SerialName("date_to") val dateTo: String? = null,
    val note: String? = null,
    @SerialName("link_url") val linkUrl: String? = null,
    /** Vazba na turnaj (spec 17.3) - udalost se pak vykresli jako odkaz na jeho popisnou stranku. */
    @SerialName("tournament_id") val tournamentId: String? = null,
    val tournament: TournamentRefDto? = null,
)

@Serializable
data class TournamentRefDto(val slug: String, val title: String)

// ---------- Turnaje (spec 13 + 17) ----------

const val TOURNAMENT_BRACKET = "bracket"
const val TOURNAMENT_BONUS = "bonus"
const val TOURNAMENT_SIMPLE = "simple"

const val TOURNAMENT_PHASE_QUALIFICATION = "qualification"
const val TOURNAMENT_PHASE_PLAYOFF = "playoff"

val TOURNAMENT_TYPES = listOf(
    TOURNAMENT_BRACKET to "Pavouk (kvalifikace + play-off)",
    TOURNAMENT_BONUS to "Bonifikační",
    TOURNAMENT_SIMPLE to "Jednoduchý (jen mřížka her)",
)

val TOURNAMENT_STATUSES = listOf(
    "draft" to "Koncept",
    "open" to "Obsazovačka",
    "running" to "Probíhá",
    "finished" to "Dohráno",
)

fun tournamentTypeLabel(type: String): String = TOURNAMENT_TYPES.firstOrNull { it.first == type }?.second ?: type

fun tournamentStatusLabel(status: String): String = TOURNAMENT_STATUSES.firstOrNull { it.first == status }?.second ?: status

/** Datum turnaje; jednodenní akci nezahlcuje duplicitním datem. */
fun formatTournamentDateRange(startsAt: String?, endsAt: String?): String = when {
    startsAt.isNullOrBlank() -> "—"
    endsAt.isNullOrBlank() || endsAt == startsAt -> formatDate(startsAt)
    else -> "${formatDate(startsAt)} – ${formatDate(endsAt)}"
}

/**
 * Typově specifické parametry turnaje (spec 17.3). Drží se v `config jsonb` místo ve sloupcích,
 * protože se sady parametrů mezi typy nepřekrývají — další typ tak nepotřebuje migraci schématu.
 * Čte se do tohohle DTO (neznámé klíče se ignorují), zapisuje se přes [tournamentConfigJson],
 * které do JSONu pošle jen klíče relevantní pro daný typ.
 */
@Serializable
data class TournamentConfigDto(
    val games: Int? = null,
    @SerialName("qual_games") val qualGames: Int? = null,
    @SerialName("po_men") val poMen: Int? = null,
    @SerialName("po_women") val poWomen: Int? = null,
    @SerialName("po_games_per_match") val poGamesPerMatch: Int? = null,
    @SerialName("bracket_reset") val bracketReset: Boolean? = null,
    @SerialName("third_place_match") val thirdPlaceMatch: Boolean? = null,
    @SerialName("bonus_pct") val bonusPct: Int? = null,
    @SerialName("split_avg") val splitAvg: Double? = null,
    @SerialName("split_date") val splitDate: String? = null,
    @SerialName("show_groups") val showGroups: Boolean = true,
)

fun defaultTournamentConfig(type: String): TournamentConfigDto = when (type) {
    TOURNAMENT_BRACKET -> TournamentConfigDto(
        qualGames = 3, poMen = 24, poWomen = 16, poGamesPerMatch = 2,
        bracketReset = true, thirdPlaceMatch = false,
    )
    TOURNAMENT_BONUS -> TournamentConfigDto(games = 4, bonusPct = 50)
    else -> TournamentConfigDto(games = 3)
}

/** Serializuje jen klíče platné pro daný typ — ať v DB nezůstávají null hodnoty cizích typů. */
fun tournamentConfigJson(type: String, config: TournamentConfigDto): JsonObject = buildJsonObject {
    put("show_groups", config.showGroups)
    when (type) {
        TOURNAMENT_BRACKET -> {
            put("qual_games", config.qualGames ?: 3)
            put("po_men", config.poMen ?: 24)
            put("po_women", config.poWomen ?: 16)
            put("po_games_per_match", config.poGamesPerMatch ?: 2)
            put("bracket_reset", config.bracketReset ?: true)
            put("third_place_match", config.thirdPlaceMatch ?: false)
        }
        TOURNAMENT_BONUS -> {
            put("games", config.games ?: 4)
            put("bonus_pct", config.bonusPct ?: 50)
            config.splitAvg?.let { put("split_avg", it) }
            config.splitDate?.let { put("split_date", it) }
        }
        else -> put("games", config.games ?: 3)
    }
}

@Serializable
data class TournamentDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    val title: String,
    val slug: String,
    val type: String = TOURNAMENT_SIMPLE,
    val status: String = "draft",
    @SerialName("starts_at") val startsAt: String? = null,
    @SerialName("ends_at") val endsAt: String? = null,
    @SerialName("body_html") val bodyHtml: String? = null,
    val config: TournamentConfigDto = TournamentConfigDto(),
) {
    /** Počet her na hráče v základní části (u pavouka kvalifikace, u ostatních celý turnaj). */
    val gamesPerPlayer: Int
        get() = when (type) {
            TOURNAMENT_BRACKET -> config.qualGames ?: 3
            TOURNAMENT_BONUS -> config.games ?: 4
            else -> config.games ?: 1
        }

    val isDraft: Boolean get() = status == "draft"
}

@Serializable
data class TournamentInput(
    @SerialName("season_id") val seasonId: String,
    val title: String,
    val slug: String,
    val type: String,
    val status: String,
    @SerialName("starts_at") val startsAt: String?,
    @SerialName("ends_at") val endsAt: String?,
    @SerialName("body_html") val bodyHtml: String?,
    val config: JsonObject,
)

/** Patch jen popisné stránky — ať uložení textu nepřepíše nastavení turnaje (a naopak). */
@Serializable
data class TournamentBodyInput(@SerialName("body_html") val bodyHtml: String)

@Serializable
data class TournamentSlotDto(
    val id: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String? = null,
    val label: String? = null,
    val capacity: Int,
    @SerialName("sort_order") val sortOrder: Int = 0,
    @SerialName("day_label") val dayLabel: String = "",
)

@Serializable
data class TournamentSlotInput(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String?,
    val label: String?,
    val capacity: Int,
    @SerialName("sort_order") val sortOrder: Int,
    @SerialName("day_label") val dayLabel: String = "",
)

@Serializable
private data class SaveTournamentSlotPlayersRequest(
    @SerialName("p_slot_id") val slotId: String,
    @SerialName("p_player_ids") val playerIds: List<String>,
    @SerialName("p_removed_entry_ids") val removedEntryIds: List<String>,
    @SerialName("p_moves") val moves: List<TournamentSlotMove>,
    @SerialName("p_guests") val guests: List<TournamentSlotGuest>,
)

@Serializable
data class TournamentSlotMove(
    @SerialName("player_id") val playerId: String,
    @SerialName("from_slot_id") val fromSlotId: String,
)

@Serializable
data class TournamentSlotGuest(
    val id: String,
    val name: String,
    val gender: String,
    @SerialName("group_code") val groupCode: String,
)

@Serializable
data class TournamentEntryDto(
    val id: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("player_id") val playerId: String,
    @SerialName("slot_id") val slotId: String? = null,
    @SerialName("group_code") val groupCode: String? = null,
    @SerialName("qual_rank") val qualRank: Int? = null,
    @SerialName("final_rank") val finalRank: Int? = null,
    val player: PlayerDto? = null,
) {
    val playerName: String get() = player?.name ?: playerId
    val gender: String? get() = player?.gender
}

@Serializable
data class CreateTournamentEntryRequest(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("player_id") val playerId: String,
    @SerialName("slot_id") val slotId: String? = null,
    @SerialName("group_code") val groupCode: String? = null,
)

@Serializable
private data class EntrySlotPatch(@SerialName("slot_id") val slotId: String?)

@Serializable
private data class SetTournamentPlayerGroupRequest(
    @SerialName("p_tournament_id") val tournamentId: String,
    @SerialName("p_player_id") val playerId: String,
    @SerialName("p_group_code") val groupCode: String?,
)

@Serializable
data class TournamentGroupingDto(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("date_from") val dateFrom: String,
    @SerialName("date_to") val dateTo: String,
    val threshold: Double,
    @SerialName("computed_at") val computedAt: String,
)

@Serializable
data class TournamentGroupPlayerDto(
    @SerialName("player_id") val playerId: String,
    @SerialName("player_name") val playerName: String,
    @SerialName("team_name") val teamName: String,
    val games: Int,
    val average: Double? = null,
    @SerialName("group_code") val groupCode: String,
)

@Serializable
private data class ComputeTournamentGroupsRequest(
    @SerialName("p_tournament_id") val tournamentId: String,
    @SerialName("p_date_from") val dateFrom: String,
    @SerialName("p_date_to") val dateTo: String,
    @SerialName("p_threshold") val threshold: Double,
)

@Serializable
private data class EntryQualRankPatch(@SerialName("qual_rank") val qualRank: Int?)

@Serializable
data class QualificationDrawRank(@SerialName("entry_id") val entryId: String, @SerialName("qual_rank") val rank: Int)

@Serializable
private data class QualificationDrawRequest(
    @SerialName("p_tournament_id") val tournamentId: String,
    @SerialName("p_ranks") val ranks: List<QualificationDrawRank>,
)

@Serializable
data class TournamentScoreDto(
    val id: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("entry_id") val entryId: String,
    @SerialName("player_id") val playerId: String,
    val score: Int,
    @SerialName("game_no") val gameNo: Int = 1,
    val phase: String = TOURNAMENT_PHASE_QUALIFICATION,
    @SerialName("is_bonus") val isBonus: Boolean = false,
    @SerialName("counts_to_ranking") val countsToRanking: Boolean = true,
)

@Serializable
data class CreateTournamentScoreRequest(
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("entry_id") val entryId: String,
    @SerialName("player_id") val playerId: String,
    val score: Int,
    @SerialName("game_no") val gameNo: Int,
    val phase: String = TOURNAMENT_PHASE_QUALIFICATION,
    @SerialName("is_bonus") val isBonus: Boolean = false,
    @SerialName("counts_to_ranking") val countsToRanking: Boolean = true,
)

@Serializable
private data class SaveTournamentDayScoresRequest(
    @SerialName("p_tournament_id") val tournamentId: String,
    @SerialName("p_slot_ids") val slotIds: List<String>,
    @SerialName("p_entry_ids") val entryIds: List<String>,
    @SerialName("p_expected_scores") val expectedScores: List<TournamentScoreDto>,
    @SerialName("p_scores") val scores: List<CreateTournamentScoreRequest>,
)

@Serializable
data class GenerateRoundSlotsRequest(
    @SerialName("p_season_id") val seasonId: String,
    @SerialName("p_total_rounds") val totalRounds: Int,
)

@Serializable
data class InsertExtraRoundSlotRequest(
    @SerialName("p_season_id") val seasonId: String,
    @SerialName("p_after_round_number") val afterRoundNumber: Int,
    @SerialName("p_league_ids") val leagueIds: List<String>,
)

@Serializable
data class DeleteExtraRoundSlotRequest(@SerialName("p_slot_id") val slotId: String)

@Serializable
data class SeasonEventInput(
    @SerialName("season_id") val seasonId: String,
    val title: String,
    val kind: String,
    @SerialName("date_from") val dateFrom: String,
    @SerialName("date_to") val dateTo: String?,
    val note: String?,
    @SerialName("link_url") val linkUrl: String?,
    @SerialName("tournament_id") val tournamentId: String? = null,
)

// ---------- CMS content (spec section 10) ----------

@Serializable
data class ContentPageDto(
    val id: String,
    val slug: String,
    val title: String,
    @SerialName("body_html") val bodyHtml: String = "",
    @SerialName("menu_order") val menuOrder: Int = 0,
    @SerialName("is_published") val isPublished: Boolean = false,
    @SerialName("updated_at") val updatedAt: String? = null,
)

@Serializable
data class ContentPageInput(
    val slug: String,
    val title: String,
    @SerialName("body_html") val bodyHtml: String,
    @SerialName("menu_order") val menuOrder: Int,
    @SerialName("is_published") val isPublished: Boolean,
)

@Serializable
data class NewsItemDto(
    val id: String,
    val title: String,
    @SerialName("body_html") val bodyHtml: String = "",
    @SerialName("is_pinned") val isPinned: Boolean = false,
    @SerialName("published_at") val publishedAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    /** "custom" (Obsah webu) | "match" (match-result) | "stats" (Statistiky) -- see NEWS_SOURCES. */
    val source: String = "custom",
)

data class NewsPage(val items: List<NewsItemDto>, val hasMore: Boolean)

const val NEWS_PAGE_SIZE = 5
private const val NEWS_CACHE_TTL_MS = 60_000
private var publicNewsGeneration = 0
private var firstNewsPage: Pair<Double, NewsPage>? = null

private fun invalidatePublicNews() {
    publicNewsGeneration++
    firstNewsPage = null
}

/** Filter options in Obsah webu → Aktuality, keyed by news_item.source. */
val NEWS_SOURCES = listOf("custom" to "Vlastní", "match" to "Zápas", "stats" to "Statistiky")

/** Edit payload; deliberately has no `source` -- the origin is set once on create and never changes. */
@Serializable
data class NewsItemInput(
    val title: String,
    @SerialName("body_html") val bodyHtml: String,
    @SerialName("is_pinned") val isPinned: Boolean,
    @SerialName("published_at") val publishedAt: String?,
)

@Serializable
private data class NewsItemCreate(
    val title: String,
    @SerialName("body_html") val bodyHtml: String,
    @SerialName("is_pinned") val isPinned: Boolean,
    @SerialName("published_at") val publishedAt: String?,
    val source: String,
)

/** `phase`: "autumn" | "spring" | "playoff" (both nadstavba groups) | null = whole season; `roundNumber` only with a phase. */
@Serializable
data class SeasonStatsRequest(
    val seasonId: String,
    val leagueId: String?,
    val phase: String?,
    val roundNumber: Int?,
    val topN: Int,
)

@Serializable
data class SeasonStatsEntryDto(
    val rank: Int,
    val score: Int,
    val playerName: String? = null,
    val teamName: String,
    val opponentTeamName: String = "",
    val opponentPlayerName: String? = null,
    val opponentScore: Int? = null,
    val leagueName: String,
    val phase: String = "",
    val roundNumber: Int = 0,
    val points: Int? = null,
    val pinDifference: Int? = null,
    val successRate: Double? = null,
    val minimumGames: Int? = null,
    val possibleGames: Int? = null,
    val games: Int? = null,
    val wins: Int? = null,
    val draws: Int? = null,
    val resultText: String? = null,
    val pinScoreText: String? = null,
    val averageScore: Double? = null,
    val totalPins: Int? = null,
    val matchCount: Int? = null,
    val share: Double? = null,
    val leagueId: String? = null,
)

@Serializable
data class SeasonStatsCategoryDto(val key: String, val entries: List<SeasonStatsEntryDto>)

@Serializable
data class UnplayedMatchDto(
    val leagueName: String,
    val phase: String,
    val roundNumber: Int,
    val homeTeamName: String,
    val awayTeamName: String,
)

@Serializable
data class SeasonStatsResponseDto(
    val totalMatches: Int,
    val completedMatches: Int,
    val unplayed: List<UnplayedMatchDto>,
    val playersWithoutGender: List<String>,
    val categories: List<SeasonStatsCategoryDto>,
)

private fun HttpRequestBuilder.auth(token: String?) {
    header("apikey", ANON_KEY)
    header("Authorization", "Bearer ${token ?: ANON_KEY}")
}

object Api {
    suspend fun login(email: String, password: String): TokenResponse =
        httpClient.post("$SUPABASE_URL/auth/v1/token?grant_type=password") {
            header("apikey", ANON_KEY)
            contentType(ContentType.Application.Json)
            setBody(mapOf("email" to email, "password" to password))
        }.body()

    /**
     * Sets the password for the user identified by `accessToken` (the session token embedded in an
     * invite/recovery email link — spec 9.9). The token stays valid for continued use afterwards,
     * so the caller can log the user straight in with it once this succeeds.
     */
    suspend fun setPassword(accessToken: String, newPassword: String) {
        httpClient.put("$SUPABASE_URL/auth/v1/user") {
            header("apikey", ANON_KEY)
            header("Authorization", "Bearer $accessToken")
            contentType(ContentType.Application.Json)
            setBody(mapOf("password" to newPassword))
        }
    }

    suspend fun fetchSeasons(token: String?): List<SeasonDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/season") {
            auth(token)
            parameter("order", "start_date.desc")
        }.body()

    suspend fun fetchSeason(token: String?, seasonId: String): SeasonDto {
        val rows: List<SeasonDto> = httpClient.get("$SUPABASE_URL/rest/v1/season") {
            auth(token)
            parameter("id", "eq.$seasonId")
        }.body()
        return rows.first()
    }

    suspend fun fetchActiveSeason(token: String?): SeasonDto? {
        val rows: List<SeasonDto> = httpClient.get("$SUPABASE_URL/rest/v1/season") {
            auth(token)
            parameter("is_active", "eq.true")
        }.body()
        return rows.firstOrNull()
    }

    suspend fun createSeason(token: String?, name: String, startDate: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/season") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(CreateSeasonRequest(name, startDate))
        }
    }

    /** Superadmin only. Sets which season is shown by default (`is_active`) -- independent of `status`. */
    suspend fun setDefaultSeason(token: String?, seasonId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/set_default_season") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("p_season_id" to seasonId))
        }
    }

    /** Superadmin only. Clears `is_active` with no replacement ("no default season" is a valid state). */
    suspend fun clearDefaultSeason(token: String?) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/clear_default_season") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(emptyMap<String, String>())
        }
    }

    /** Superadmin only. Freely moves a season between draft/active/finished -- independent of `is_active`. */
    suspend fun setSeasonStatus(token: String?, seasonId: String, status: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/set_season_status") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("p_season_id" to seasonId, "p_status" to status))
        }
    }

    /** Superadmin only; server rejects deleting a season currently in progress (`status = 'active'`). */
    suspend fun deleteSeason(token: String?, seasonId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/delete_season") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("p_season_id" to seasonId))
        }
    }

    suspend fun fetchLeagues(token: String?, seasonId: String): List<SeasonLeagueDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/season_league") {
            auth(token)
            parameter("season_id", "eq.$seasonId")
            parameter("order", "name.asc")
        }.body()

    suspend fun fetchLeague(token: String?, leagueId: String): SeasonLeagueDto {
        val rows: List<SeasonLeagueDto> = httpClient.get("$SUPABASE_URL/rest/v1/season_league") {
            auth(token)
            parameter("id", "eq.$leagueId")
        }.body()
        return rows.first()
    }

    suspend fun createLeague(
        token: String?,
        seasonId: String,
        name: String,
        teamCount: Int,
        hasPlayoff: Boolean = false,
        tier: Int? = null,
        scheduleAlgorithm: String = "berger",
    ) {
        httpClient.post("$SUPABASE_URL/rest/v1/season_league") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(CreateLeagueRequest(seasonId, name, teamCount, hasPlayoff, tier, scheduleAlgorithm))
        }
    }

    suspend fun updateLeague(token: String?, leagueId: String, hasPlayoff: Boolean, teamCount: Int, tier: Int?, scheduleAlgorithm: String) {
        httpClient.patch("$SUPABASE_URL/rest/v1/season_league") {
            auth(token)
            parameter("id", "eq.$leagueId")
            contentType(ContentType.Application.Json)
            setBody(UpdateLeagueRequest(hasPlayoff, teamCount, tier, scheduleAlgorithm))
        }
    }

    /** Založí novou sezónu z předlohy (postup/sestup + přebrané soupisky a průměry) — supabase/functions/season-clone. */
    suspend fun cloneSeason(token: String?, name: String, startDate: String, previousSeasonId: String, promotionCount: Int): SeasonDto {
        val result: CloneSeasonResponseDto = httpClient.post("$SUPABASE_URL/functions/v1/season-clone") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(CloneSeasonRequest(name, startDate, previousSeasonId, promotionCount))
        }.body()
        return fetchSeason(token, result.seasonId)
    }

    suspend fun splitPlayoff(token: String?, leagueId: String): PlayoffSplitResponseDto =
        httpClient.post("$SUPABASE_URL/functions/v1/playoff-split") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(PlayoffSplitRequest(leagueId))
        }.body()

    suspend fun fetchAllTeams(token: String?): List<TeamDto> =
        singleFlight("fetchAllTeams:$token") {
            httpClient.get("$SUPABASE_URL/rest/v1/team") {
                auth(token)
                parameter("order", "name.asc")
            }.body()
        }

    suspend fun fetchLeagueTeams(token: String?, leagueId: String): List<SeasonLeagueTeamDto> =
        singleFlight("fetchLeagueTeams:$token:$leagueId") {
            httpClient.get("$SUPABASE_URL/rest/v1/season_league_team") {
                auth(token)
                parameter("season_league_id", "eq.$leagueId")
                parameter("select", "*,team:team_id(*)")
                parameter("order", "seed.asc.nullslast")
            }.body()
        }

    /** Team ids already assigned to ANY league within the given season (spec 3, Krok 3). */
    suspend fun fetchAssignedTeamIdsForSeason(token: String?, seasonId: String): Set<String> {
        val rows: List<Map<String, JsonElement>> =
            httpClient.get("$SUPABASE_URL/rest/v1/season_league_team") {
                auth(token)
                parameter("select", "team_id,season_league!inner(season_id)")
                parameter("season_league.season_id", "eq.$seasonId")
            }.body()
        return rows.mapNotNull { it["team_id"]?.let { v -> (v as? JsonPrimitive)?.content } }.toSet()
    }

    /** Every team assigned to any league of the season, season-scoped (so team_name_override resolves correctly) —
     * used instead of [fetchAllTeams] wherever team names must reflect the season being viewed, not the live/global name. */
    suspend fun fetchAllTeamsForSeason(token: String?, seasonId: String): List<SeasonLeagueTeamDto> =
        singleFlight("fetchAllTeamsForSeason:$token:$seasonId") {
            httpClient.get("$SUPABASE_URL/rest/v1/season_league_team") {
                auth(token)
                parameter("select", "*,team:team_id(*),season_league!inner(season_id)")
                parameter("season_league.season_id", "eq.$seasonId")
            }.body()
        }

    suspend fun createTeam(token: String?, name: String, averageScore: Double?): TeamDto {
        val created: List<TeamDto> = httpClient.post("$SUPABASE_URL/rest/v1/team") {
            auth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(CreateTeamRequest(name, averageScore))
        }.body()
        return created.first()
    }

    suspend fun updateTeam(token: String?, teamId: String, name: String, averageScore: Double?) {
        httpClient.patch("$SUPABASE_URL/rest/v1/team") {
            auth(token)
            parameter("id", "eq.$teamId")
            contentType(ContentType.Application.Json)
            setBody(UpdateTeamRequest(name, averageScore))
        }
    }

    suspend fun assignTeamToLeague(token: String?, leagueId: String, teamId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/season_league_team") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(AssignTeamRequest(leagueId, teamId))
        }
    }

    /** Průměr přebraný z minulé sezóny (season_league_team.average_score), na rozdíl od [updateTeam]'s globálního team.average_score. */
    suspend fun updateSeasonLeagueTeamAverage(token: String?, seasonLeagueTeamId: String, averageScore: Double?) {
        httpClient.patch("$SUPABASE_URL/rest/v1/season_league_team") {
            auth(token)
            parameter("id", "eq.$seasonLeagueTeamId")
            contentType(ContentType.Application.Json)
            setBody(UpdateSeasonLeagueTeamAverageRequest(averageScore))
        }
    }

    /** Přejmenuje tým a zpětně zamrazí staré jméno (team_name_override) na všech jeho dosavadních
     * přiřazeních do ligy, která ještě žádné dobové jméno nemají — staré sezóny tak dál ukazují staré jméno. */
    /**
     * Renames a team, freezing its old name (team_name_override) onto every already-FINISHED
     * season's assignment that doesn't have one yet -- an active/draft season keeps showing the
     * live (now new) name. Moved server-side (supabase/functions/rename-team) since it needs a
     * two-level join to know which assignments are "finished" and must clear the standings/
     * player-stats/player-of-round caches, which only service_role can write.
     */
    suspend fun renameTeam(token: String?, teamId: String, newName: String) {
        httpClient.post("$SUPABASE_URL/functions/v1/rename-team") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("teamId" to teamId, "newName" to newName))
        }
    }

    suspend fun fetchAllPlayers(token: String?): List<PlayerDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/player") {
            auth(token)
            parameter("order", "name.asc")
        }.body()

    suspend fun fetchRoster(token: String?, teamId: String, seasonId: String): List<RosterEntryDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("team_id", "eq.$teamId")
            parameter("season_id", "eq.$seasonId")
            parameter("select", "*,player:player_id(*)")
        }.body()

    /** Soupisky všech týmů v sezóně naráz (pro přehledovou stránku Soupisky), místo 1 dotazu na tým. */
    suspend fun fetchRosterForSeason(token: String?, seasonId: String): List<RosterEntryDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("season_id", "eq.$seasonId")
            parameter("select", "*,player:player_id(*)")
        }.body()

    /** Player ids already on ANY team's roster for the given season (spec 6: max 1 team/season). */
    suspend fun fetchRosteredPlayerIdsForSeason(token: String?, seasonId: String): Set<String> {
        val rows: List<Map<String, JsonElement>> = httpClient.get("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("select", "player_id")
            parameter("season_id", "eq.$seasonId")
        }.body()
        return rows.mapNotNull { it["player_id"]?.let { v -> (v as? JsonPrimitive)?.content } }.toSet()
    }

    /** Roster size per team for the given season (max 6, spec 6). */
    suspend fun fetchRosterCountsForSeason(token: String?, seasonId: String): Map<String, Int> {
        val rows: List<Map<String, JsonElement>> = httpClient.get("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("select", "team_id")
            parameter("season_id", "eq.$seasonId")
        }.body()
        return rows.mapNotNull { it["team_id"]?.let { v -> (v as? JsonPrimitive)?.content } }
            .groupingBy { it }
            .eachCount()
    }

    suspend fun createPlayer(
        token: String?,
        name: String,
        fullName: String?,
        gender: String?,
        registered: Boolean,
        card: Boolean,
        email: String?,
        phone: String?,
    ): PlayerDto {
        val created: List<PlayerDto> = httpClient.post("$SUPABASE_URL/rest/v1/player") {
            auth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(CreatePlayerRequest(name, fullName, gender, registered, card, email, phone))
        }.body()
        return created.first()
    }

    suspend fun updatePlayer(
        token: String?,
        playerId: String,
        name: String,
        fullName: String?,
        gender: String?,
        registered: Boolean,
        card: Boolean,
        email: String?,
        phone: String?,
    ) {
        httpClient.patch("$SUPABASE_URL/rest/v1/player") {
            auth(token)
            parameter("id", "eq.$playerId")
            contentType(ContentType.Application.Json)
            setBody(UpdatePlayerRequest(name, fullName, gender, registered, card, email, phone))
        }
    }

    suspend fun deletePlayer(token: String?, playerId: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/player") {
            auth(token)
            parameter("id", "eq.$playerId")
        }
    }

    /** Player ids on ANY team's roster in ANY season — used to decide if a player is deletable. */
    suspend fun fetchRosteredPlayerIdsAnywhere(token: String?): Set<String> {
        val rows: List<Map<String, JsonElement>> = httpClient.get("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("select", "player_id")
        }.body()
        return rows.mapNotNull { it["player_id"]?.let { v -> (v as? JsonPrimitive)?.content } }.toSet()
    }

    suspend fun addToRoster(token: String?, playerId: String, teamId: String, seasonId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(AddRosterRequest(playerId, teamId, seasonId))
        }
    }

    suspend fun removeFromRoster(token: String?, rosterEntryId: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/player_season_roster") {
            auth(token)
            parameter("id", "eq.$rosterEntryId")
        }
    }

    /** Nastaví/zruší kapitána soupisky. Při nastavení (true) zruší dosavadního kapitána téhož týmu
     * v sezóně (právě 1) a vyžaduje, aby měl hráč vyplněný email + telefon (jinak DB odmítne). */
    suspend fun setRosterCaptain(token: String?, rosterEntryId: String, captain: Boolean) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/set_roster_captain") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(SetRosterCaptainRequest(rosterEntryId, captain))
        }
    }

    suspend fun fetchMatches(token: String?, leagueId: String): List<MatchDto> =
        singleFlight("fetchMatches:$token:$leagueId") {
            httpClient.get("$SUPABASE_URL/rest/v1/match") {
                auth(token)
                parameter("season_league_id", "eq.$leagueId")
                parameter("order", "phase.asc,round_number.asc")
            }.body()
        }

    suspend fun fetchMatch(token: String?, matchId: String): MatchDto {
        val rows: List<MatchDto> = httpClient.get("$SUPABASE_URL/rest/v1/match") {
            auth(token)
            parameter("id", "eq.$matchId")
        }.body()
        return rows.first()
    }

    /** All matches across every league of a season, tagged with the league name (public schedule "all leagues" tab). */
    suspend fun fetchMatchesForSeason(
        token: String?, seasonId: String, roundNumbers: List<Int>? = null,
    ): List<MatchWithLeagueDto> {
        if (roundNumbers != null && roundNumbers.isEmpty()) return emptyList()
        val rounds = roundNumbers?.distinct()?.sorted()?.joinToString(",")
        return singleFlight("fetchMatchesForSeason:$token:$seasonId:$rounds:$publicNewsGeneration") {
            val matches = mutableListOf<MatchWithLeagueDto>()
            // Stay below PostgREST's row cap. ID breaks ties at page boundaries.
            do {
                val page: List<MatchWithLeagueDto> = httpClient.get("$SUPABASE_URL/rest/v1/match") {
                    auth(token)
                    parameter("select", "id,season_league_id,home_team_id,away_team_id,round_number,phase,status," +
                        "home_points,away_points,home_duel_points,away_duel_points,season_league!inner(name)")
                    parameter("season_league.season_id", "eq.$seasonId")
                    if (rounds != null) parameter("round_number", "in.($rounds)")
                    parameter("order", "phase.asc,round_number.asc,id.asc")
                    parameter("offset", matches.size)
                    parameter("limit", 500)
                }.body()
                matches.addAll(page)
            } while (page.size == 500)
            matches.toList()
        }
    }

    suspend fun fetchPublicSeasonOverview(seasonId: String): PublicSeasonOverviewDto =
        singleFlight("publicSeasonOverview:$seasonId:$publicNewsGeneration") {
            httpClient.get("$SUPABASE_URL/rest/v1/rpc/public_season_overview") {
                auth(null)
                parameter("p_season_id", seasonId)
            }.body()
        }

    suspend fun fetchMatchDuels(token: String?, matchId: String): List<MatchDuelDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/match_duel") {
            auth(token)
            parameter("match_id", "eq.$matchId")
            parameter("order", "sequence.asc")
        }.body()

    suspend fun fetchMatchLineup(token: String?, matchId: String): List<MatchLineupDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/match_lineup") {
            auth(token)
            parameter("match_id", "eq.$matchId")
            parameter("select", "*,player:player_id(*)")
        }.body()

    /** seasonId scopes to a whole season (global ranking); leagueId scopes to one league. Pass one of the two. */
    suspend fun fetchPlayerStats(token: String?, seasonId: String? = null, leagueId: String? = null): List<PlayerStatDto> {
        val key = "fetchPlayerStats:$token:$seasonId:$leagueId"
        return singleFlight(key) {
            conditionalGet<PlayerStatsResponseDto>(key, "$SUPABASE_URL/functions/v1/player-stats") {
                auth(token)
                seasonId?.let { parameter("seasonId", it) }
                leagueId?.let { parameter("leagueId", it) }
            }.players
        }
    }

    suspend fun fetchPlayerOfRound(token: String?, leagueId: String, phase: String, roundNumber: Int): PlayerOfRoundResponseDto {
        val key = "fetchPlayerOfRound:$token:$leagueId:$phase:$roundNumber"
        return conditionalGet(key, "$SUPABASE_URL/functions/v1/player-of-round") {
            auth(token)
            parameter("leagueId", leagueId)
            parameter("phase", phase)
            parameter("roundNumber", roundNumber)
        }
    }

    /** Every league's every played round in one call, instead of one request per (league, phase, round). */
    suspend fun fetchPlayerOfRoundSeasonBatch(token: String?, seasonId: String): List<SeasonRoundNomineesDto> {
        val key = "fetchPlayerOfRoundSeasonBatch:$token:$seasonId"
        return singleFlight(key) {
            conditionalGet<PlayerOfRoundSeasonBatchDto>(key, "$SUPABASE_URL/functions/v1/player-of-round") {
                auth(token)
                parameter("seasonId", seasonId)
            }.rounds
        }
    }

    suspend fun generateSchedule(token: String?, leagueId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/generate_schedule") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(mapOf("p_league_id" to leagueId))
        }
    }

    // ---------- Termínová listina: round slots ----------
    suspend fun fetchRoundSlots(token: String?, seasonId: String): List<SeasonRoundSlotDto> =
        singleFlight("fetchRoundSlots:$token:$seasonId") {
            httpClient.get("$SUPABASE_URL/rest/v1/season_round_slot") {
                auth(token)
                parameter("season_id", "eq.$seasonId")
                parameter("select", "*,season_round_slot_league(season_league_id)")
                parameter("order", "round_number.asc")
            }.body()
        }

    /** Idempotent — regenerating replaces the season's existing slots (same pattern as generateSchedule). */
    suspend fun generateRoundSlots(token: String?, seasonId: String, totalRounds: Int) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/generate_season_round_slots") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(GenerateRoundSlotsRequest(seasonId, totalRounds))
        }
    }

    suspend fun updateRoundSlot(token: String?, id: String, dateFrom: String, dateTo: String?) {
        httpClient.patch("$SUPABASE_URL/rest/v1/season_round_slot") {
            auth(token)
            parameter("id", "eq.$id")
            contentType(ContentType.Application.Json)
            setBody(mapOf("date_from" to dateFrom, "date_to" to dateTo))
        }
    }

    /**
     * Inserts a round window exclusive to the given leagues (e.g. a bigger league that needs more
     * rounds than the rest of the season) right after an existing round number, shifting every later
     * round (shared or already-exclusive) up by one number and 14 days. Call before generating those
     * leagues' (and every other league's) rozpis -- generate_schedule bakes round numbers/dates into
     * match_round at generation time, so inserting afterwards requires regenerating affected leagues.
     */
    suspend fun insertExtraRoundSlot(token: String?, seasonId: String, afterRoundNumber: Int, leagueIds: List<String>) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/insert_extra_round_slot") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(InsertExtraRoundSlotRequest(seasonId, afterRoundNumber, leagueIds))
        }
    }

    /** Removes a league-exclusive round slot, shifting everything after it back down. */
    suspend fun deleteExtraRoundSlot(token: String?, slotId: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/delete_extra_round_slot") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(DeleteExtraRoundSlotRequest(slotId))
        }
    }

    // ---------- Termínová listina: freeform events ----------
    suspend fun fetchSeasonEvents(token: String?, seasonId: String): List<SeasonEventDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/season_event") {
            auth(token)
            parameter("season_id", "eq.$seasonId")
            // Turnaj se tahá s sebou, aby šel řádek rovnou vykreslit jako odkaz na jeho stránku
            // (spec 17.3) bez druhého dotazu; u konceptu ho RLS nevrátí a odkaz se nezobrazí.
            parameter("select", "*,tournament:tournament_id(slug,title)")
            parameter("order", "date_from.asc")
        }.body()

    suspend fun createSeasonEvent(token: String?, input: SeasonEventInput) {
        httpClient.post("$SUPABASE_URL/rest/v1/season_event") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun updateSeasonEvent(token: String?, id: String, input: SeasonEventInput) {
        httpClient.patch("$SUPABASE_URL/rest/v1/season_event") {
            auth(token)
            parameter("id", "eq.$id")
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun deleteSeasonEvent(token: String?, id: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/season_event") {
            auth(token)
            parameter("id", "eq.$id")
        }
    }

    // ---------- Turnaje (spec 13 + 17) ----------

    /** Veřejně vrací jen turnaje, které nejsou koncept — filtruje RLS (spec 17.11), ne tenhle dotaz. */
    suspend fun fetchTournaments(token: String?, seasonId: String): List<TournamentDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            parameter("season_id", "eq.$seasonId")
            parameter("order", "starts_at.desc.nullslast,created_at.desc")
        }.body()

    suspend fun fetchTournamentBySlug(token: String?, seasonId: String, slug: String): TournamentDto? {
        val rows: List<TournamentDto> = httpClient.get("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            parameter("season_id", "eq.$seasonId")
            parameter("slug", "eq.$slug")
            parameter("limit", "1")
        }.body()
        return rows.firstOrNull()
    }

    suspend fun createTournament(token: String?, input: TournamentInput): TournamentDto {
        val created: List<TournamentDto> = httpClient.post("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(input)
        }.body()
        return created.first()
    }

    suspend fun updateTournament(token: String?, tournamentId: String, input: TournamentInput) {
        httpClient.patch("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            parameter("id", "eq.$tournamentId")
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun updateTournamentBody(token: String?, tournamentId: String, bodyHtml: String) {
        httpClient.patch("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            parameter("id", "eq.$tournamentId")
            contentType(ContentType.Application.Json)
            setBody(TournamentBodyInput(bodyHtml))
        }
    }

    suspend fun deleteTournament(token: String?, tournamentId: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/tournament") {
            auth(token)
            parameter("id", "eq.$tournamentId")
        }
    }

    suspend fun fetchTournamentGrouping(token: String?, tournamentId: String): TournamentGroupingDto? {
        val rows: List<TournamentGroupingDto> = httpClient.get("$SUPABASE_URL/rest/v1/tournament_grouping") {
            auth(token)
            parameter("tournament_id", "eq.$tournamentId")
        }.body()
        return rows.firstOrNull()
    }

    suspend fun fetchTournamentGroupPlayers(token: String?, tournamentId: String): List<TournamentGroupPlayerDto> {
        val rows = mutableListOf<TournamentGroupPlayerDto>()
        while (true) {
            val page: List<TournamentGroupPlayerDto> = httpClient.get("$SUPABASE_URL/rest/v1/tournament_group_player") {
                auth(token)
                parameter("tournament_id", "eq.$tournamentId")
                parameter("order", "player_id.asc")
                parameter("offset", rows.size.toString())
                parameter("limit", "500")
            }.body()
            if (page.isEmpty()) return rows
            rows.addAll(page)
        }
    }

    suspend fun computeTournamentGroups(token: String?, tournamentId: String, dateFrom: String, dateTo: String, threshold: Double) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/compute_tournament_groups") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(ComputeTournamentGroupsRequest(tournamentId, dateFrom, dateTo, threshold))
        }
    }

    // ---------- Obsazovačka (spec 17.4) ----------

    suspend fun saveTournamentSlotPlayers(
        token: String?, slotId: String, playerIds: List<String>, removedEntryIds: List<String>,
        moves: List<TournamentSlotMove> = emptyList(), guests: List<TournamentSlotGuest> = emptyList(),
    ) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/save_tournament_slot_players") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(SaveTournamentSlotPlayersRequest(slotId, playerIds, removedEntryIds, moves, guests))
        }
    }

    suspend fun fetchTournamentSlots(token: String?, tournamentId: String): List<TournamentSlotDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/tournament_slot") {
            auth(token)
            parameter("tournament_id", "eq.$tournamentId")
            parameter("order", "sort_order.asc,starts_at.asc")
        }.body()

    suspend fun createTournamentSlot(token: String?, input: TournamentSlotInput) {
        httpClient.post("$SUPABASE_URL/rest/v1/tournament_slot") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun updateTournamentSlot(token: String?, slotId: String, input: TournamentSlotInput) {
        httpClient.patch("$SUPABASE_URL/rest/v1/tournament_slot") {
            auth(token)
            parameter("id", "eq.$slotId")
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    /** Smazání slotu účastníky nemaže — `slot_id` na nich jen spadne na null (on delete set null). */
    suspend fun deleteTournamentSlot(token: String?, slotId: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/tournament_slot") {
            auth(token)
            parameter("id", "eq.$slotId")
        }
    }

    // ---------- Účastníci (spec 17.3) ----------

    suspend fun fetchTournamentEntries(token: String?, tournamentId: String): List<TournamentEntryDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/tournament_entry") {
            auth(token)
            parameter("tournament_id", "eq.$tournamentId")
            parameter("select", "*,player:player_id(*)")
            parameter("order", "created_at.asc")
        }.body()

    suspend fun createTournamentEntry(
        token: String?,
        tournamentId: String,
        playerId: String,
        slotId: String? = null,
        groupCode: String? = null,
    ): TournamentEntryDto {
        val created: List<TournamentEntryDto> = httpClient.post("$SUPABASE_URL/rest/v1/tournament_entry") {
            auth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(CreateTournamentEntryRequest(tournamentId, playerId, slotId, groupCode))
        }.body()
        return created.first()
    }

    suspend fun deleteTournamentEntry(token: String?, entryId: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/tournament_entry") {
            auth(token)
            parameter("id", "eq.$entryId")
        }
    }

    // Patch only the slot: encodeDefaults=true must not clear other registration fields.
    suspend fun setTournamentEntrySlot(token: String?, entryId: String, slotId: String?) {
        httpClient.patch("$SUPABASE_URL/rest/v1/tournament_entry") {
            auth(token)
            parameter("id", "eq.$entryId")
            contentType(ContentType.Application.Json)
            setBody(EntrySlotPatch(slotId))
        }
    }

    /** Changes the saved group and any registration together, without recalculating averages. */
    suspend fun setTournamentPlayerGroup(token: String?, tournamentId: String, playerId: String, groupCode: String?) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/set_tournament_player_group") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(SetTournamentPlayerGroupRequest(tournamentId, playerId, groupCode))
        }
    }

    suspend fun saveTournamentQualificationDraw(token: String?, tournamentId: String, ranks: List<QualificationDrawRank>) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/save_tournament_qualification_draw") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(QualificationDrawRequest(tournamentId, ranks))
        }
    }

    suspend fun setTournamentEntryQualRank(token: String?, entryId: String, qualRank: Int?) {
        httpClient.patch("$SUPABASE_URL/rest/v1/tournament_entry") {
            auth(token)
            parameter("id", "eq.$entryId")
            contentType(ContentType.Application.Json)
            setBody(EntryQualRankPatch(qualRank))
        }
    }

    // ---------- Skóre ----------

    suspend fun fetchTournamentScores(token: String?, tournamentId: String): List<TournamentScoreDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/tournament_score") {
            auth(token)
            parameter("tournament_id", "eq.$tournamentId")
            parameter("order", "game_no.asc")
        }.body()

    suspend fun saveTournamentDayScores(
        token: String?, tournamentId: String, slotIds: List<String>, entryIds: List<String>,
        expectedScores: List<TournamentScoreDto>, scores: List<CreateTournamentScoreRequest>,
    ): List<TournamentScoreDto> =
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/save_tournament_day_scores") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(SaveTournamentDayScoresRequest(tournamentId, slotIds, entryIds, expectedScores, scores))
        }.body()

    suspend fun fetchStandings(token: String?, leagueId: String, phase: String? = null): StandingsResponseDto {
        val key = "fetchStandings:$token:$leagueId:$phase"
        return singleFlight(key) {
            conditionalGet(key, "$SUPABASE_URL/functions/v1/standings") {
                auth(token)
                parameter("leagueId", leagueId)
                phase?.let { parameter("phase", it) }
            }
        }
    }

    suspend fun submitMatchResult(
        token: String?,
        matchId: String,
        duels: List<DuelInput>,
        lineup: LineupInput?,
        playedAt: String?,
        protocolImagePath: String? = null,
    ) {
        httpClient.post("$SUPABASE_URL/functions/v1/match-result") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(MatchResultRequest(matchId, duels, lineup, playedAt, protocolImagePath = protocolImagePath))
        }
        invalidatePublicNews()
    }

    /** Kontumace (admin/superadmin only, spec 4.x): awards the match 3:0 on league points to the
     * non-forfeiting side with no games played, for a match that never actually happened (e.g. a
     * team not showing up) -- no lineup/duels to enter. */
    suspend fun submitForfeit(token: String?, matchId: String, forfeitingSide: String, playedAt: String?) {
        httpClient.post("$SUPABASE_URL/functions/v1/match-result") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(MatchResultRequest(matchId = matchId, playedAt = playedAt, forfeit = forfeitingSide))
        }
        invalidatePublicNews()
    }

    // ---------- CMS: content pages ----------
    /** Published pages for public; admins additionally see drafts (RLS decides based on the token). */
    suspend fun fetchContentPages(token: String?, includeBody: Boolean = true): List<ContentPageDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/content_page") {
            auth(token)
            if (!includeBody) parameter("select", "id,slug,title,menu_order,is_published,updated_at")
            parameter("order", "menu_order.asc,title.asc")
        }.body()

    suspend fun fetchContentPage(token: String?, slug: String): ContentPageDto? {
        val rows: List<ContentPageDto> = httpClient.get("$SUPABASE_URL/rest/v1/content_page") {
            auth(token)
            parameter("slug", "eq.$slug")
        }.body()
        return rows.firstOrNull()
    }

    suspend fun createContentPage(token: String?, input: ContentPageInput) {
        httpClient.post("$SUPABASE_URL/rest/v1/content_page") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun updateContentPage(token: String?, id: String, input: ContentPageInput) {
        httpClient.patch("$SUPABASE_URL/rest/v1/content_page") {
            auth(token)
            parameter("id", "eq.$id")
            contentType(ContentType.Application.Json)
            setBody(input)
        }
    }

    suspend fun deleteContentPage(token: String?, id: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/content_page") {
            auth(token)
            parameter("id", "eq.$id")
        }
    }

    // ---------- CMS: news items (aktuality) ----------
    /** Public feed only. One lookahead row avoids a separate COUNT query. Cache just the first
     * page, for returns to Home; drafts/admin responses never enter this cache. */
    suspend fun fetchPublicNewsPage(offset: Int = 0): NewsPage {
        require(offset >= 0)
        if (offset == 0) firstNewsPage?.let { (expiresAt, page) ->
            if (kotlin.js.Date.now() < expiresAt) return page
        }
        val generation = publicNewsGeneration
        return singleFlight("publicNews:$generation:$offset") {
            val rows: List<NewsItemDto> = httpClient.get("$SUPABASE_URL/rest/v1/news_item") {
                auth(null)
                parameter("published_at", "not.is.null")
                parameter("order", "is_pinned.desc,published_at.desc.nullslast,id.desc")
                parameter("offset", offset)
                parameter("limit", NEWS_PAGE_SIZE + 1)
            }.body()
            val page = NewsPage(rows.take(NEWS_PAGE_SIZE), rows.size > NEWS_PAGE_SIZE)
            // A mutation during the GET must not repopulate the cache with its old response.
            if (offset == 0 && generation == publicNewsGeneration) {
                firstNewsPage = (kotlin.js.Date.now() + NEWS_CACHE_TTL_MS) to page
            }
            page
        }
    }

    /** Pinned first, then newest published; drafts (null published_at) sort last (admin list only). */
    suspend fun fetchNews(token: String?): List<NewsItemDto> =
        httpClient.get("$SUPABASE_URL/rest/v1/news_item") {
            auth(token)
            parameter("order", "is_pinned.desc,published_at.desc.nullslast,id.desc")
        }.body()

    /** Returns the created row (e.g. so Statistiky can open its freshly created draft in the editor). */
    suspend fun createNews(token: String?, input: NewsItemInput, source: String = "custom"): NewsItemDto {
        val rows: List<NewsItemDto> = httpClient.post("$SUPABASE_URL/rest/v1/news_item") {
            auth(token)
            header("Prefer", "return=representation")
            contentType(ContentType.Application.Json)
            setBody(NewsItemCreate(input.title, input.bodyHtml, input.isPinned, input.publishedAt, source))
        }.body()
        invalidatePublicNews()
        return rows.single()
    }

    suspend fun fetchNewsItem(token: String?, id: String): NewsItemDto? =
        httpClient.get("$SUPABASE_URL/rest/v1/news_item") {
            auth(token)
            parameter("id", "eq.$id")
        }.body<List<NewsItemDto>>().firstOrNull()

    // ---------- Statistiky (admin, spec 17) ----------
    suspend fun fetchSeasonStats(token: String?, request: SeasonStatsRequest): SeasonStatsResponseDto =
        httpClient.post("$SUPABASE_URL/functions/v1/season-stats") {
            auth(token)
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun updateNews(token: String?, id: String, input: NewsItemInput) {
        httpClient.patch("$SUPABASE_URL/rest/v1/news_item") {
            auth(token)
            parameter("id", "eq.$id")
            contentType(ContentType.Application.Json)
            setBody(input)
        }
        invalidatePublicNews()
    }

    suspend fun deleteNews(token: String?, id: String) {
        httpClient.delete("$SUPABASE_URL/rest/v1/news_item") {
            auth(token)
            parameter("id", "eq.$id")
        }
        invalidatePublicNews()
    }

    /**
     * Uploads a picked File to the public content-images bucket and returns its public URL. Uses the
     * browser fetch API (rather than Ktor) so the raw File/Blob body streams without manual byte copying.
     */
    suspend fun uploadContentImage(token: String?, path: String, file: File, bucket: String = "content-images"): String {
        val headers = kotlin.js.json(
            "apikey" to ANON_KEY,
            "Authorization" to "Bearer ${token ?: ANON_KEY}",
            "Content-Type" to file.type,
        )
        val resp = window.fetch(
            "$SUPABASE_URL/storage/v1/object/$bucket/$path",
            RequestInit(method = "POST", headers = headers, body = file),
        ).await()
        if (!resp.ok) throw RuntimeException("Nahrání obrázku selhalo (HTTP ${resp.status}).")
        return "$SUPABASE_URL/storage/v1/object/public/$bucket/$path"
    }

    // ---------- Match protocol photo (spec 10.9) ----------
    /** One object per match (`$matchId.jpg`, always JPEG post-resize) — `x-upsert` lets a re-upload
     * on the same match simply overwrite instead of accumulating orphans on every edit. */
    suspend fun uploadMatchProtocolImage(token: String?, matchId: String, blob: Blob): String {
        val path = "$matchId.jpg"
        val headers = kotlin.js.json(
            "apikey" to ANON_KEY,
            "Authorization" to "Bearer ${token ?: ANON_KEY}",
            "Content-Type" to "image/jpeg",
            "x-upsert" to "true",
        )
        val resp = window.fetch(
            "$SUPABASE_URL/storage/v1/object/match-protocols/$path",
            RequestInit(method = "POST", headers = headers, body = blob),
        ).await()
        if (!resp.ok) throw RuntimeException("Nahrání fotky selhalo (HTTP ${resp.status}).")
        return path
    }

    fun matchProtocolImageUrl(path: String): String = "$SUPABASE_URL/storage/v1/object/public/match-protocols/$path"
}
